import React, { useState } from 'react';
import LiveTranscription from './LiveTranscription';
import VisitNotesEditor from './VisitNotesEditor';
import TemplatesManager from './TemplatesManager';
import SessionSummary from './SessionSummary';
import AnkrCases from './AnkrCases';
import EvaluationEngine from '../evaluation/EvaluationEngine';
import { remove } from '../../utils/sessionStorage';

/**
 * ScribePanel - AI-powered medical documentation panel
 * Contains transcription, note editing, templates, and session management
 */
const ScribePanel = ({ 
  soapNote, transcript, speakerSegments, onUpdateNote, providerInfo, 
  authData, isAuthenticated, onMinimizedChange, activeTab, onTabChange, updateUrlWithTab, caseId
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tabsContainerRef = React.useRef(null);
  

  // Available tabs
  const tabs = [
    { id: 'cases', name: 'Cases', icon: '🗂️' },
    { id: 'transcription', name: 'Transcripts', icon: '🎤' },
    { id: 'notes', name: 'Notes', icon: '📝' },
    { id: 'templates', name: 'Templates', icon: '📄' },
    { id: 'session', name: 'Session', icon: '📊' },
    { id: 'quanta-eval', name: 'Quanta', icon: '🧠' }
  ];

  // Toggle minimize/maximize
  const toggleMinimized = () => {
    const newMinimizedState = !isMinimized;
    setIsMinimized(newMinimizedState);
    if (onMinimizedChange) {
      onMinimizedChange(newMinimizedState);
    }
  };

  // Handle expand button click when minimized
  const handleExpand = () => {
    setIsMinimized(false);
    if (onMinimizedChange) {
      onMinimizedChange(false);
    }
  };
  
  // Check scroll availability
  const checkScrollAvailability = React.useCallback(() => {
    if (!tabsContainerRef.current) return;
    
    const container = tabsContainerRef.current;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
  }, []);

  // Scroll tabs container
  const scrollTabs = (direction) => {
    if (!tabsContainerRef.current) return;
    
    const container = tabsContainerRef.current;
    const scrollAmount = 150;
    
    if (direction === 'left') {
      container.scrollLeft -= scrollAmount;
    } else {
      container.scrollLeft += scrollAmount;
    }
    
    setTimeout(checkScrollAvailability, 100);
  };

  // Check scroll on mount and resize
  React.useEffect(() => {
    checkScrollAvailability();
    window.addEventListener('resize', checkScrollAvailability);
    return () => window.removeEventListener('resize', checkScrollAvailability);
  }, [checkScrollAvailability]);

  // Handle clearing authentication state
  const handleClearAuth = () => {
    try {
      // Clear localStorage auth data
      remove('sol_auth_data');
      // Show success message and reload page
      alert('You were logged out successfully. The page will reload.');
      // Reload the page to reset all state
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      alert('Error clearing authentication data');
    }
  };

  return (
    <div className="scribe-panel-wrapper">
      {!isMinimized && (<button 
        className="scribe-toggle-button" 
        onClick={toggleMinimized}
        title={isMinimized ? "Expand Scribe Panel" : "Minimize Scribe Panel"}
        style={{ left: isMinimized? '-47px':'-57px'}}
      >
        <i className={`fa fa-bars ${!isMinimized ? 'fa-lg' : ''}`}></i>
      </button>)}
      
      <div className={`scribe-panel-container ${isMinimized ? 'minimized' : ''}`}>
        {/* Minimized state - only show expand button */}
        {isMinimized && (
          <div className="minimized-panel">
            {isMinimized && (<button 
              className="scribe-toggle-button" 
              onClick={toggleMinimized}
              title={isMinimized ? "Expand Scribe Panel" : "Minimize Scribe Panel"}
              style={{ left: isMinimized? '-47px':'-57px',top:0}}
            >
              <i className={`fa fa-bars ${!isMinimized ? 'fa-lg' : ''}`}></i>
            </button>)}
            <div className="minimized-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`minimized-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => {
                  onTabChange(tab.id);
                  handleExpand();
                }}
                title={tab.name}
              >
                <span className="tab-icon">{tab.icon}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full panel state */}
      {!isMinimized && (
        <>
          {/* Header with tabs and controls */}
          <div className="scribe-panel-header">
            {canScrollLeft && (
              <button
                className="scroll-button scroll-left"
                onClick={() => scrollTabs('left')}
                title="Scroll left"
              >
                ◀
              </button>
            )}
            
            <div 
              className="scribe-tabs"
              ref={tabsContainerRef}
              onScroll={checkScrollAvailability}
            >
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`scribe-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => onTabChange(tab.id)}
                  title={tab.name}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-name">{tab.name}</span>
                </button>
              ))}
            </div>
            
            {canScrollRight && (
              <button
                className="scroll-button scroll-right"
                onClick={() => scrollTabs('right')}
                title="Scroll right"
              >
                ▶
              </button>
            )}
          </div>

          {/* Content area */}
          <div className="scribe-panel-content">
            {activeTab === 'cases' && (
              <AnkrCases
                providerInfo={providerInfo}
                authData={authData}
                isAuthenticated={isAuthenticated}
                updateUrlWithTab={updateUrlWithTab}
                onTabChange={onTabChange}
              />
            )}

            {activeTab === 'transcription' && (
              <LiveTranscription
                transcript={transcript}
                speakerSegments={speakerSegments}
                caseId={caseId}
              />
            )}

            {activeTab === 'notes' && (
              <VisitNotesEditor
                soapNote={soapNote}
                onUpdateNote={onUpdateNote}
                providerInfo={providerInfo}
                authData={authData}
                caseId={caseId}
              />
            )}

            {activeTab === 'templates' && (
              <TemplatesManager
                onApplyTemplate={(templateData) => {
                  // Handle template application
                  if (templateData.mode === 'full') {
                    onUpdateNote(templateData.template.content);
                  } else {
                    // Handle section insertion
                    const currentNote = soapNote || '';
                    const updatedNote = currentNote + '\n\n' + templateData.template.content;
                    onUpdateNote(updatedNote);
                  }
                }}
                providerInfo={providerInfo}
                authData={authData}
                isAuthenticated={isAuthenticated}
              />
            )}

            {activeTab === 'session' && (
              <SessionSummary
                transcript={transcript}
                speakerSegments={speakerSegments}
                soapNote={soapNote}
                providerInfo={providerInfo}
              />
            )}

            {activeTab === 'quanta-eval' && (
              <EvaluationEngine
                authData={authData}
                providerInfo={providerInfo}
                onEvaluationComplete={(result) => {
                  console.log('Evaluation complete:', result);
                }}
              />
            )}
          </div>

          {/* Status bar */}
          <div className="scribe-status-bar">
            <div className="status-left">
              {transcript && (
                <span className="status-indicator">
                  <span className="indicator-dot recording"></span>
                  Recording
                </span>
              )}
            </div>
            
            <div className="status-right">
              {isAuthenticated && providerInfo?.name ? (
                <span className="provider-name">{providerInfo.name}</span>
              ) : (
                <span className="provider-name">No provider is logged in</span>
              )}              
              {isAuthenticated && (
                <button onClick={handleClearAuth} className="button secondary" title="Clear authentication data" >
                  Logout </button>
              )}
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default ScribePanel;